"use client"

import { useEffect, useRef, type ReactElement } from "react"

import {
  TRADINGVIEW_COPYRIGHT,
  type TradingViewConfig,
} from "@modules/trader-menu/lib/widgets"

// Story 10.7 — embed reutilizável dos widgets TradingView.
//
// Porquê injeção por DOM e não `<script>` em JSX: o loader do TradingView carrega o widget lendo
// o PRÓPRIO `innerHTML` (o JSON de config). React NÃO executa um `<script>` filho colocado em JSX
// nem via `dangerouslySetInnerHTML`, por isso o script é criado e anexado por DOM no `useEffect`
// (padrão canónico do TradingView).
//
// O effect do strict-mode (dev) corre 2×; sem guarda empilharia dois widgets. O `injectedRef`
// + o cleanup (esvazia o container e baixa a flag) garantem exatamente UM widget montado e
// limpeza correta no unmount real.
//
// A dependência do effect é o `config` SERIALIZADO (`configJson`), não a referência do objeto: os
// callers de hoje passam constantes de módulo (identidade estável), mas depender da string protege
// qualquer caller futuro que passe um literal inline de re-injetar (e re-vazar) o widget a cada
// render. Wrapper `overflow-x-auto` para os widgets de largura fixa (heatmap 1004, gráfico 1400)
// não estourarem o mobile — chrome de layout nosso; o `config` de conteúdo é verbatim.

export interface TradingViewWidgetProps {
  /** Loader `https://s3.tradingview.com/external-embedding/<nome>.js`. */
  scriptSrc: string
  /** Config VERBATIM do widget (serializada para o `innerHTML` do script). */
  config: TradingViewConfig
  /** Nome acessível do painel (o iframe injetado pelo terceiro não é rotulável por nós). */
  title: string
}

export default function TradingViewWidget({
  scriptSrc,
  config,
  title,
}: TradingViewWidgetProps): ReactElement {
  const widgetRef = useRef<HTMLDivElement | null>(null)
  const injectedRef = useRef(false)
  // Chave estável por VALOR (não por identidade do objeto) — ver comentário do cabeçalho.
  const configJson = JSON.stringify(config)

  useEffect(() => {
    const widget = widgetRef.current
    if (!widget || injectedRef.current) {
      return
    }
    injectedRef.current = true

    const script = document.createElement("script")
    script.src = scriptSrc
    script.async = true
    script.type = "text/javascript"
    script.innerHTML = configJson
    widget.appendChild(script)

    return () => {
      // Baixa a flag E esvazia o container: no double-invoke do strict-mode a 2.ª passagem
      // reinjeta um único widget; no unmount real remove o script e o iframe do terceiro.
      injectedRef.current = false
      widget.innerHTML = ""
    }
  }, [scriptSrc, configJson])

  return (
    <div className="w-full max-w-full overflow-x-auto">
      <div
        className="tradingview-widget-container"
        role="region"
        aria-label={title}
      >
        <div
          className="tradingview-widget-container__widget"
          ref={widgetRef}
        />
        <div className="tradingview-widget-copyright mt-2 text-xs text-muted">
          <a
            href={TRADINGVIEW_COPYRIGHT.href}
            target="_blank"
            rel="noopener noreferrer"
            className="text-subtle transition-colors hover:text-text"
          >
            {TRADINGVIEW_COPYRIGHT.label}
          </a>
        </div>
      </div>
    </div>
  )
}
